import dayjs from 'dayjs';

import { type TNumberFormatterOptions, formatNumber } from './numberFormatter';

const COMMON_DATE_FORMAT = 'DD.MM.YYYY, HH:mm';

export const parsePrice = (num: string): number => {
  return parseFloat(num.replace(/\$\s?|( *)/g, '').replace(',', '.'));
};

export const formatPrice = (
  num: number | string,
  options?: TNumberFormatterOptions
): string => {
  const { fallback = '', ...restOptions } = options || {};

  const formatResult = formatNumber(num, {
    ...restOptions,
    fallback
  });

  return formatResult.formattedValue;
};

export const unformatPrice = (value: string) => {
  return value.replace(/\s/g, '');
};

export const formatPhoneNumber = (
  value: string | null | undefined,
  defaultValue: string = ''
): string => {
  if (!value) return defaultValue;
  const digits = value.replace(/\D/g, '');
  const match = digits.match(/^7(\d{3})(\d{3})(\d{2})(\d{2})$/);

  if (!match) return value;

  const [, area, first, second, third] = match;
  return `+7 (${area}) ${first}-${second}-${third}`;
};

export const onlyDigits = (value: string) => {
  return value.replace(/\D/g, '');
};

export const cleanNameValue = (value: string) => {
  const cleanedNameValue = value.replace(/\d+/g, '').trim().toLowerCase();
  return cleanedNameValue;
};

export const cleanFullNameValue = (value: string) => {
  const cleanedNameValue = value
    .replace(/[^a-zA-Zа-яА-ЯёЁӘәҒғҚқҢңӨөҰұҮүҺһІі\- ]/g, '')
    .replace(/([\s-]){2,}/g, '$1')
    .trimStart();
  const capitalizedValue = cleanedNameValue
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return capitalizedValue;
};

export const formatDate = (date: string | null | undefined) => {
  if (!date) return '';
  return dayjs(date).format(COMMON_DATE_FORMAT);
};

/**
 * Форматирует строковую дату в формат "18 апреля 2025 г. 13:45:05"
 * @param dateString - Строка даты, совместимая с Date (например, "2025-04-18T13:45:05Z")
 * @returns Отформатированная строка или сообщение об ошибке при неверной дате
 */
export function formatLabeledFullDate(
  dateString: string,
  config?: {
    hideTime?: boolean;
    hideCurrentYear?: boolean;
    hideSeconds?: boolean;
  }
): string {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return 'Неверная дата';
  }

  const {
    hideTime = false,
    hideCurrentYear = false,
    hideSeconds = false
  } = config || {};

  const day = date.getDate().toString().padStart(2, '0');
  const monthNames = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря'
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  const resultDate = [`${day} ${month}`];

  if (!hideCurrentYear || year !== new Date().getFullYear())
    resultDate.push(`${year} г.`);

  if (!hideTime) {
    let time = `${hours}:${minutes}`;
    if (!hideSeconds) time += `:${seconds}`;
    resultDate.push(time);
  }

  return resultDate.join(' ');
}
